import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Mail, UserPlus, Send, CheckCircle, AlertCircle, Clock, 
  RefreshCw, User, GraduationCap, Users 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UserInvitationPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: 'student',
    // Student-specific fields
    date_of_birth: '',
    gender: 'M',
    nationality: 'Française',
    address: '',
    postal_code: '',
    city: '',
    training_start_date: new Date().toISOString().split('T')[0],
    training_end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    funding_type: 'autofinancement',
    // Trainer-specific fields
    specialization: '',
    bio: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRoleChange = (value) => {
    setFormData(prev => ({ ...prev, role: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Fetch pending invitations
  const fetchPendingInvitations = useCallback(async () => {
    setLoadingInvitations(true);
    try {
      // Fetch students with pending_activation status
      const studentsRes = await fetch(`${API_URL}/api/students`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const students = studentsRes.ok ? await studentsRes.json() : [];
      
      // Fetch trainers
      const trainersRes = await fetch(`${API_URL}/api/trainers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const trainers = trainersRes.ok ? await trainersRes.json() : [];

      // Combine and format
      const pendingStudents = students
        .filter(s => s.status === 'pending_activation' || !s.is_activated)
        .map(s => ({
          id: s.id,
          email: s.email,
          first_name: s.first_name,
          last_name: s.last_name,
          role: 'student',
          status: s.status || 'pending_activation',
          is_activated: s.is_activated
        }));

      const pendingTrainers = trainers
        .filter(t => t.status === 'pending_activation' || !t.is_activated)
        .map(t => ({
          id: t.id,
          email: t.email,
          first_name: t.first_name,
          last_name: t.last_name,
          role: 'trainer',
          status: t.status || 'pending_activation',
          is_activated: t.is_activated
        }));

      setPendingInvitations([...pendingStudents, ...pendingTrainers]);
    } catch (error) {
      console.error('Error fetching invitations:', error);
    } finally {
      setLoadingInvitations(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPendingInvitations();
  }, [fetchPendingInvitations]);

  const handleSendInvitation = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let endpoint, body;

      if (formData.role === 'student') {
        endpoint = `${API_URL}/api/students`;
        body = {
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone: formData.phone,
          date_of_birth: formData.date_of_birth,
          gender: formData.gender,
          nationality: formData.nationality,
          address: formData.address,
          postal_code: formData.postal_code,
          city: formData.city,
          training_start_date: formData.training_start_date,
          training_end_date: formData.training_end_date,
          funding_type: formData.funding_type
        };
      } else {
        endpoint = `${API_URL}/api/trainers`;
        body = {
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone: formData.phone,
          specialization: formData.specialization,
          bio: formData.bio
        };
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(
          `Invitation envoyée à ${formData.first_name} ${formData.last_name}`,
          { description: `Un email d'activation a été envoyé à ${formData.email}` }
        );
        
        // Reset form
        setFormData({
          email: '',
          first_name: '',
          last_name: '',
          phone: '',
          role: 'student',
          date_of_birth: '',
          gender: 'M',
          nationality: 'Française',
          address: '',
          postal_code: '',
          city: '',
          training_start_date: new Date().toISOString().split('T')[0],
          training_end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          funding_type: 'autofinancement',
          specialization: '',
          bio: ''
        });
        
        // Refresh pending list
        fetchPendingInvitations();
      } else {
        toast.error(data.detail || "Erreur lors de l'envoi de l'invitation");
      }
    } catch (error) {
      toast.error("Erreur de connexion au serveur");
    } finally {
      setLoading(false);
    }
  };

  const resendInvitation = async (invitation) => {
    try {
      const endpoint = invitation.role === 'student' 
        ? `${API_URL}/api/students/${invitation.id}/resend-activation`
        : `${API_URL}/api/trainers/${invitation.id}/resend-activation`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(`Email renvoyé à ${invitation.email}`);
      } else {
        const data = await response.json();
        toast.error(data.detail || "Erreur lors du renvoi");
      }
    } catch (error) {
      toast.error("Erreur de connexion");
    }
  };

  const getStatusBadge = (status, isActivated) => {
    if (isActivated) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
          <CheckCircle className="h-3 w-3" />
          Activé
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">
        <Clock className="h-3 w-3" />
        En attente
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="user-invitation-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inviter des Utilisateurs</h1>
          <p className="text-gray-500 mt-1">
            Créez des comptes et envoyez des invitations par email
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchPendingInvitations}
          disabled={loadingInvitations}
          data-testid="refresh-invitations-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loadingInvitations ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Invitation Form */}
        <Card data-testid="invitation-form-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-violet-600" />
              Nouvelle Invitation
            </CardTitle>
            <CardDescription>
              Remplissez les informations pour créer un compte et envoyer l'invitation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSendInvitation} className="space-y-4">
              {/* Role Selection */}
              <div className="space-y-2">
                <Label>Type d'utilisateur</Label>
                <Select value={formData.role} onValueChange={handleRoleChange}>
                  <SelectTrigger data-testid="invitation-role-select">
                    <SelectValue placeholder="Sélectionner un rôle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="student">
                      <span className="flex items-center gap-2">
                        <GraduationCap className="h-4 w-4" />
                        Étudiant
                      </span>
                    </SelectItem>
                    <SelectItem value="trainer">
                      <span className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        Formateur
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">Prénom *</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    placeholder="Jean"
                    required
                    data-testid="invitation-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Nom *</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    placeholder="Dupont"
                    required
                    data-testid="invitation-last-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="jean.dupont@example.com"
                  required
                  data-testid="invitation-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Téléphone</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="06 12 34 56 78"
                  data-testid="invitation-phone"
                />
              </div>

              {/* Student-specific fields */}
              {formData.role === 'student' && (
                <div className="space-y-4 pt-4 border-t">
                  <h4 className="font-medium text-sm text-gray-700">Informations étudiant</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="date_of_birth">Date de naissance *</Label>
                      <Input
                        id="date_of_birth"
                        name="date_of_birth"
                        type="date"
                        value={formData.date_of_birth}
                        onChange={handleInputChange}
                        required
                        data-testid="invitation-dob"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Genre *</Label>
                      <Select value={formData.gender} onValueChange={(v) => handleSelectChange('gender', v)}>
                        <SelectTrigger data-testid="invitation-gender">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="M">Homme</SelectItem>
                          <SelectItem value="F">Femme</SelectItem>
                          <SelectItem value="O">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address">Adresse *</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="123 Rue de la Formation"
                      required
                      data-testid="invitation-address"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="postal_code">Code postal *</Label>
                      <Input
                        id="postal_code"
                        name="postal_code"
                        value={formData.postal_code}
                        onChange={handleInputChange}
                        placeholder="75001"
                        required
                        data-testid="invitation-postal"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">Ville *</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="Paris"
                        required
                        data-testid="invitation-city"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="training_start_date">Début formation *</Label>
                      <Input
                        id="training_start_date"
                        name="training_start_date"
                        type="date"
                        value={formData.training_start_date}
                        onChange={handleInputChange}
                        required
                        data-testid="invitation-start-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="training_end_date">Fin formation *</Label>
                      <Input
                        id="training_end_date"
                        name="training_end_date"
                        type="date"
                        value={formData.training_end_date}
                        onChange={handleInputChange}
                        required
                        data-testid="invitation-end-date"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Type de financement *</Label>
                    <Select value={formData.funding_type} onValueChange={(v) => handleSelectChange('funding_type', v)}>
                      <SelectTrigger data-testid="invitation-funding">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="autofinancement">Autofinancement</SelectItem>
                        <SelectItem value="cpf">CPF</SelectItem>
                        <SelectItem value="entreprise">Entreprise</SelectItem>
                        <SelectItem value="pole_emploi">Pôle Emploi</SelectItem>
                        <SelectItem value="opco">OPCO</SelectItem>
                        <SelectItem value="autre">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Trainer-specific fields */}
              {formData.role === 'trainer' && (
                <div className="space-y-4 pt-4 border-t">
                  <h4 className="font-medium text-sm text-gray-700">Informations formateur</h4>
                  
                  <div className="space-y-2">
                    <Label htmlFor="specialization">Spécialisation</Label>
                    <Input
                      id="specialization"
                      name="specialization"
                      value={formData.specialization}
                      onChange={handleInputChange}
                      placeholder="Développement Web, Data Science..."
                      data-testid="invitation-specialization"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bio">Biographie</Label>
                    <Textarea
                      id="bio"
                      name="bio"
                      value={formData.bio}
                      onChange={handleInputChange}
                      placeholder="Courte présentation du formateur..."
                      rows={3}
                      data-testid="invitation-bio"
                    />
                  </div>
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full bg-violet-600 hover:bg-violet-700" 
                disabled={loading}
                data-testid="send-invitation-btn"
              >
                {loading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Création en cours...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Créer et envoyer l'invitation
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Pending Invitations */}
        <Card data-testid="pending-invitations-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-violet-600" />
              Invitations en attente
              {pendingInvitations.length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded-full">
                  {pendingInvitations.length}
                </span>
              )}
            </CardTitle>
            <CardDescription>
              Utilisateurs qui n'ont pas encore activé leur compte
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingInvitations ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
              </div>
            ) : pendingInvitations.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-300" />
                <p className="font-medium">Aucune invitation en attente</p>
                <p className="text-sm">Tous les utilisateurs ont activé leur compte</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {pendingInvitations.map((inv) => (
                  <div 
                    key={`${inv.role}-${inv.id}`}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100"
                    data-testid={`invitation-item-${inv.role}-${inv.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        inv.role === 'student' ? 'bg-blue-100' : 'bg-emerald-100'
                      }`}>
                        {inv.role === 'student' ? (
                          <GraduationCap className="h-5 w-5 text-blue-600" />
                        ) : (
                          <User className="h-5 w-5 text-emerald-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {inv.first_name} {inv.last_name}
                        </p>
                        <p className="text-sm text-gray-500">{inv.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(inv.status, inv.is_activated)}
                      {!inv.is_activated && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => resendInvitation(inv)}
                          className="text-violet-600 hover:text-violet-700"
                          data-testid={`resend-btn-${inv.role}-${inv.id}`}
                        >
                          <Mail className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Info Box */}
      <Card className="bg-gradient-to-r from-violet-50 to-indigo-50 border-violet-200">
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center flex-shrink-0">
              <Mail className="h-6 w-6 text-violet-600" />
            </div>
            <div>
              <h3 className="font-semibold text-violet-900">Comment fonctionne l'invitation ?</h3>
              <ul className="mt-2 text-sm text-violet-800 space-y-1">
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-violet-200 text-violet-700 flex items-center justify-center text-xs font-medium">1</span>
                  Remplissez les informations et sélectionnez le rôle
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-violet-200 text-violet-700 flex items-center justify-center text-xs font-medium">2</span>
                  Un email d'activation est envoyé automatiquement
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-violet-200 text-violet-700 flex items-center justify-center text-xs font-medium">3</span>
                  L'utilisateur clique sur le lien et définit son mot de passe
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-violet-200 text-violet-700 flex items-center justify-center text-xs font-medium">4</span>
                  Le compte est activé et prêt à l'emploi
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
